import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { currentTheme as currentVizTheme, refreshTheme } from 'devextreme/viz/themes';
import { current } from 'devextreme/ui/themes';
import notify from 'devextreme/ui/notify';

export interface ThemeFamily {
  id: string;
  label: string;
  group: string;
  /** Color principal para mostrar como puntito de preview. */
  swatch: string;
}

// Familias disponibles. Tienen que existir los 4 archivos en /assets/themes:
// dx.{id}.light.css / dark.css / light.compact.css / dark.compact.css.
export const themeFamilies: ThemeFamily[] = [
  { id: 'fluent.blue',     label: 'Fluent · Azul',     group: 'Fluent',   swatch: '#0078d4' },
  { id: 'fluent.saas',     label: 'Fluent · SaaS',     group: 'Fluent',   swatch: '#6366f1' },
  { id: 'material.blue',   label: 'Material · Azul',   group: 'Material', swatch: '#1976d2' },
  { id: 'material.teal',   label: 'Material · Teal',   group: 'Material', swatch: '#00897b' },
  { id: 'material.purple', label: 'Material · Violeta',group: 'Material', swatch: '#8e24aa' },
  { id: 'material.orange', label: 'Material · Naranja',group: 'Material', swatch: '#f57c00' },
  { id: 'material.lime',   label: 'Material · Lima',   group: 'Material', swatch: '#cddc39' },
];

export type ThemeMode = 'light' | 'dark';
export type ThemeDensity = 'normal' | 'compact';

const STORAGE_FAMILY  = 'crm.theme.family';
const STORAGE_MODE    = 'crm.theme.mode';
const STORAGE_DENSITY = 'crm.theme.density';
const LINK_ID         = 'dx-theme-link';
const COMMON_LINK_ID  = 'dx-common-link';

const DEFAULT_FAMILY  = 'fluent.blue';
const DEFAULT_MODE: ThemeMode = 'light';
const DEFAULT_DENSITY: ThemeDensity = 'normal';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  family  = (localStorage.getItem(STORAGE_FAMILY) || DEFAULT_FAMILY);
  mode    = ((localStorage.getItem(STORAGE_MODE) as ThemeMode) || DEFAULT_MODE);
  density = ((localStorage.getItem(STORAGE_DENSITY) as ThemeDensity) || DEFAULT_DENSITY);

  get currentTheme(): ThemeMode { return this.mode; }
  get currentDensity(): ThemeDensity { return this.density; }
  isDark = new BehaviorSubject<boolean>(this.mode === 'dark');
  isCompact = new BehaviorSubject<boolean>(this.density === 'compact');

  private silent = true;  // No notify durante el setAppTheme inicial.

  setAppTheme(): void {
    console.info('[theme] aplicando', this.family, this.mode, this.density);
    this.applyDxThemeLink();
    this.applyVariablesBundle();
    this.refreshDxRuntime();
    this.applyBodyAttrs();
    this.isDark.next(this.mode === 'dark');
    this.isCompact.next(this.density === 'compact');
    if (!this.silent) {
      const f = themeFamilies.find(x => x.id === this.family);
      const compactSuffix = this.density === 'compact' ? ' · compacto' : '';
      notify({
        message: `${f?.label ?? this.family} · ${this.mode === 'dark' ? 'oscuro' : 'claro'}${compactSuffix}`,
        type: 'info',
        displayTime: 1800,
        position: { my: 'bottom right', at: 'bottom right', offset: '-24 -24' },
        animation: {
          show: { type: 'slide', duration: 280, from: { translateX: 320, opacity: 0 }, to: { translateX: 0, opacity: 1 } },
          hide: { type: 'fade',  duration: 220, to: { opacity: 0 } },
        },
      });
    }
    this.silent = false;
  }

  setTheme(family: string, mode: ThemeMode, density?: ThemeDensity): void {
    this.family = family;
    this.mode = mode;
    if (density) this.density = density;
    localStorage.setItem(STORAGE_FAMILY, family);
    localStorage.setItem(STORAGE_MODE, mode);
    localStorage.setItem(STORAGE_DENSITY, this.density);
    this.setAppTheme();
  }

  /**
   * Cambio de modo claro/oscuro — se aplica en caliente sin recargar.
   * Las variables del template (vars-light/dark) se togglean via stylesheet.disabled.
   */
  switchTheme(): void {
    this.setTheme(this.family, this.mode === 'dark' ? 'light' : 'dark', this.density);
  }
  setMode(mode: ThemeMode): void { this.setTheme(this.family, mode, this.density); }

  /**
   * Toggle de densidad (normal ↔ compact). Se aplica en caliente swappeando el
   * stylesheet de DevExtreme (los archivos .compact.css existen para cada combo).
   */
  toggleDensity(): void {
    this.setDensity(this.density === 'compact' ? 'normal' : 'compact');
  }
  setDensity(density: ThemeDensity): void {
    this.density = density;
    localStorage.setItem(STORAGE_DENSITY, density);
    this.applyDxThemeLink();
    this.applyBodyAttrs();
    this.isCompact.next(density === 'compact');
    if (!this.silent) {
      notify({
        message: density === 'compact' ? 'Modo compacto activado' : 'Densidad estándar',
        type: 'info',
        displayTime: 1500,
        position: { my: 'bottom right', at: 'bottom right', offset: '-24 -24' },
      });
    }
  }

  /**
   * Cambio de FAMILIA — el CSS de DevExtreme tiene cientos de reglas que pisan estilos
   * y un swap en caliente puede fallar por orden de carga / specificity. Persistimos
   * la elección y recargamos. El preloader (en index.html) lee localStorage y aplica
   * el tema correcto antes del bootstrap, garantizando que se vea sin glitches.
   */
  setFamily(family: string): void {
    if (family === this.family) return;
    this.family = family;
    localStorage.setItem(STORAGE_FAMILY, family);
    const f = themeFamilies.find(x => x.id === family);
    notify({
      message: `Cambiando a ${f?.label ?? family}…`,
      type: 'info',
      displayTime: 700,
      position: { my: 'bottom right', at: 'bottom right', offset: '-24 -24' },
    });
    setTimeout(() => location.reload(), 600);
  }

  getFamilies(): ThemeFamily[] { return themeFamilies; }
  isFluent(): boolean { return current().includes('fluent'); }

  /* ===== Internals ===== */

  private applyDxThemeLink(): void {
    this.ensureLink(COMMON_LINK_ID, 'assets/themes/dx.common.css');
    // Densidad compacta usa archivos `.compact.css`. Si no existen para una
    // familia, el browser dejará el link viejo y log de error — el set de
    // archivos generados cubre las 7 familias × 2 modos × 2 densidades.
    const compact = this.density === 'compact' ? '.compact' : '';
    const href = `assets/themes/dx.${this.family}.${this.mode}${compact}.css`;
    this.ensureLink(LINK_ID, href);
  }

  private ensureLink(id: string, href: string): void {
    let link = document.getElementById(id) as HTMLLinkElement | null;
    const absolute = new URL(href, document.baseURI).href;

    if (link && link.href === absolute) {
      console.info('[theme] CSS ya cargado, skip:', href);
      return;
    }

    // Hot-swap: creamos el nuevo link, esperamos onload, y recien ahi removemos el viejo.
    // Esto evita FOUC (flash of unstyled content) durante el cambio de tema.
    const next = document.createElement('link');
    next.rel = 'stylesheet';
    next.href = href;
    next.id = id + '__pending';

    const old = link;
    next.onload = () => {
      console.info('[theme] CSS cargado:', href);
      next.id = id;
      if (old && old !== next) old.remove();
      try { refreshTheme(); } catch {}
    };
    next.onerror = (err) => {
      console.error('[theme] CSS falló al cargar:', href, err);
      next.remove();
    };
    document.head.appendChild(next);
  }

  // Habilita el bundle vars-{mode} y deshabilita el otro.
  private applyVariablesBundle(): void {
    const sheets = Array.from(document.styleSheets);
    for (const s of sheets) {
      const href = (s as CSSStyleSheet).href ?? '';
      if (href.includes('vars-light')) {
        (s as CSSStyleSheet).disabled = this.mode !== 'light';
      } else if (href.includes('vars-dark')) {
        (s as CSSStyleSheet).disabled = this.mode !== 'dark';
      }
    }
  }

  // Atributo en <body> para que estilos custom puedan reaccionar al tema.
  private applyBodyAttrs(): void {
    document.body.setAttribute('data-theme-family', this.family);
    document.body.setAttribute('data-theme-mode', this.mode);
    document.body.setAttribute('data-theme-density', this.density);
    document.body.classList.toggle('dx-theme-dark', this.mode === 'dark');
    document.body.classList.toggle('dx-theme-light', this.mode === 'light');
    document.body.classList.toggle('dx-theme-compact', this.density === 'compact');
  }

  private refreshDxRuntime(): void {
    try {
      // viz themes no soportan suffix `.compact`, usamos el base para charts.
      currentVizTheme(`${this.family}.${this.mode}`);
      refreshTheme();
    } catch (err) {
      console.warn('ThemeService: refreshDxRuntime fallo (no critico)', err);
    }
  }
}
